// De kolommen van het overzicht van een wervingsactie, op één plek voor het
// scherm (Rollup.jsx) én de CSV-export: zo kunnen die twee niet uit elkaar
// lopen. Het scherm zet telling en percentage samen in een cel ('3 (75%)'),
// de export zet ze in aparte kolommen.
//
// AI-CONTEXT: See docs/features/wervingsacties-registratie.md (Uitbreiding 2026-10 (2))
// AI-CONTEXT: See docs/patterns.md#ui-csv-export

import { UITKOMST_DETAIL_OPTIONS } from './registratieOptions'
import { dienstKeuzes } from './resolveWervingsDiensten'

/** @type {Array<{ id: 'wijk' | 'buurt' | 'straat' | 'postcode', label: string, kolom: string }>} */
const NIVEAUS = [
    { id: 'wijk', label: 'Per wijk', kolom: 'Wijk' },
    { id: 'buurt', label: 'Per buurt', kolom: 'Buurt' },
    { id: 'straat', label: 'Per straat', kolom: 'Straat' },
    { id: 'postcode', label: 'Per postcode', kolom: 'Postcode' },
]

const UITKOMST_NAAM = { ja: 'Ja', nee: 'Nee', misschien: 'Misschien' }

/**
 * @typedef {object} RollupKolom
 * @property {string} key - Uniek binnen de kolommen
 * @property {string} label - Kop op het scherm en in de export
 * @property {(row: object) => (number | string | null)} value - null = onbekend
 * @property {(row: object) => (number | null)} [pct] - Percentage bij een telling; null als er geen basis was
 * @property {boolean} [alleenPerRegel] - Leeg in de totaalregel (stand en opmerking van één straat)
 */

/**
 * @param {object} args
 * @param {'wijk' | 'buurt' | 'straat' | 'postcode'} args.niveau
 * @param {Array<{ key: string, label: string }>} args.diensten - De zichtbare diensten van de organisatie
 * @param {Object<string, string>} [args.dienstNamen] - Sleutel → naam, ook van verborgen diensten
 * @param {Array<string>} [args.gebruikteDiensten] - Sleutels die in de telling voorkomen (ook verborgen)
 * @param {Object<string, { Tekst?: string }>} [args.straatOpmerkingen] - Per straatKey (alleen op straatniveau)
 * @returns {Array<RollupKolom>}
 */
const rollupColumns = ({ niveau, diensten, dienstNamen = {}, gebruikteDiensten = [], straatOpmerkingen = {} }) => {
    const gebied = NIVEAUS.find((n) => n.id === niveau)?.kolom || 'Gebied'

    const telling = (key, label, pctKey) => ({
        key,
        label,
        value: (row) => row[key],
        ...(pctKey ? { pct: (row) => row[pctKey] } : {}),
    })

    const redenen = Object.entries(UITKOMST_DETAIL_OPTIONS).flatMap(([uitkomst, opties]) => opties.map((o) => ({
        key: `detail-${uitkomst}-${o.value}`,
        label: `${UITKOMST_NAAM[uitkomst]}: ${o.label}`,
        value: (row) => row.details?.[uitkomst]?.[o.value] || 0,
    })))

    const dienstKolommen = dienstKeuzes(diensten, gebruikteDiensten, dienstNamen).map((d) => ({
        key: `dienst-${d.key}`,
        label: `Doet mee: ${d.label}`,
        value: (row) => row.diensten?.[d.key] || 0,
    }))

    const straten = niveau === 'straat'
        ? [
            {
                key: 'stand',
                label: 'Stand',
                alleenPerRegel: true,
                // Adressen zonder straatnaam ('Overig') zijn geen straat met een stand.
                value: (row) => (row.key === '' ? '' : row.straten?.klaar ? 'Klaar' : row.straten?.bezig ? 'Bezig' : 'Nog te doen'),
            },
            {
                key: 'opmerking',
                label: 'Opmerking',
                alleenPerRegel: true,
                value: (row) => (straatOpmerkingen[row.key]?.Tekst || '').trim(),
            },
        ]
        : [
            { key: 'straten-nog', label: 'Straten nog te doen', value: (row) => row.straten?.nog },
            { key: 'straten-bezig', label: 'Straten bezig', value: (row) => row.straten?.bezig ?? 0 },
            { key: 'straten-klaar', label: 'Straten klaar', value: (row) => row.straten?.klaar ?? 0 },
        ]

    return [
        { key: 'gebied', label: gebied, value: (row) => row.label },
        { key: 'inGebied', label: 'Woningen in gebied', value: (row) => row.inGebied },
        telling('totaal', 'Gekozen'),
        telling('briefGepost', 'Brief gepost', 'briefGepostPct'),
        telling('deurgesprek', 'Deurgesprek', 'deurgesprekPct'),
        telling('ja', 'Ja', 'jaPct'),
        telling('nee', 'Nee', 'neePct'),
        telling('misschien', 'Misschien', 'misschienPct'),
        ...redenen,
        ...dienstKolommen,
        telling('huur', 'Huur', 'huurPct'),
        telling('koop', 'Koop', 'koopPct'),
        ...straten,
    ]
}

/**
 * De tekst in een cel op het scherm: telling met percentage, of 'onbekend'.
 *
 * @param {RollupKolom} kolom
 * @param {object} row
 * @returns {string}
 */
const celTekst = (kolom, row) => {
    const waarde = kolom.value(row)
    if (waarde === null || waarde === undefined) return 'onbekend'
    const procent = kolom.pct ? kolom.pct(row) : null
    return procent === null || procent === undefined ? String(waarde) : `${waarde} (${procent}%)`
}

export { NIVEAUS, rollupColumns, celTekst }
